import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";

// ADV-881: minimal client for the activity_events read API. Powers the
// "Last role change" detail card on the member detail page.

interface ActivityEventRow {
  id: string;
  business_id: string | null;
  account_id: string;
  event_type: string;
  entity_type: string | null;
  entity_id: string | null;
  actor_type: "user" | "api_key" | "system";
  actor_id: string | null;
  source: string;
  origin_type: string | null;
  origin_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

interface ListResponse {
  activity_events: ActivityEventRow[];
  total: number;
}

interface ActivityEventFilters {
  /** Filter to a single event_type (e.g. "entity.updated"). */
  eventType?: string;
  /** Filter to a single entity (combine with eventType for a narrow query). */
  entityType?: string;
  entityId?: string;
  /** Cap the result set. Lets callers ask the server for "newest 1" instead of
   * paginating client-side. Default service-side cap is 50. */
  limit?: number;
}

/**
 * Server-side filtered query over /core/activity-events. Use this when you
 * want the most recent event of a specific type for a specific entity —
 * scanning a paginated list client-side is unreliable when the entity has
 * many events of other types (Codex P2 on ADV-881 PR #679).
 */
export function useActivityEvents(filters: ActivityEventFilters) {
  const params = new URLSearchParams();
  if (filters.eventType) params.set("event_type", filters.eventType);
  if (filters.entityType) params.set("entity_type", filters.entityType);
  if (filters.entityId) params.set("entity_id", filters.entityId);
  if (filters.limit !== undefined) params.set("limit", String(filters.limit));
  const qs = params.toString();

  return useQuery({
    queryKey: ["activity-events", "list", filters],
    queryFn: () =>
      api.get<ListResponse>(`/core/activity-events${qs ? `?${qs}` : ""}`),
    enabled: !filters.entityId || !!filters.entityId,
  });
}
